import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, Minus, Sparkles, AlertTriangle } from "lucide-react";
import { getStatusTokens, getSignalTokens, getDisplayTokens } from "@/lib/status";
import { fmtPrice } from "@/lib/format";
import { DecisionStrip } from "@/components/DecisionStrip";
import { useTranslation } from "@/lib/i18n";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * ScorePanel — the hero output block on the analyze page.
 *
 * Color rule (post-redesign): the panel's overall color follows the
 * SIGNAL (Opportunity / Neutral / Risk), not the raw valuation status.
 * This keeps the AI Opportunity Score, the headline color and the chips
 * logically consistent — a value-trap that's mathematically undervalued
 * appears as Neutral/Yellow, not as a green opportunity.
 */
export default function ScorePanel({ valuation, price, animateKey }) {
  const { t } = useTranslation();
  if (!valuation) return null;
  // Feb 2026 — Valuation-safety unavailable state (from
  // ``valuation_engine.compute_fair_value``'s split guard).  When
  // the backend cannot defensibly compute a fair value (proven
  // currency/share-unit mismatch OR unverified extreme outlier),
  // it returns status=signal="Unavailable" and every numeric field
  // is ``null``.  Render an honest empty-state score panel that
  // NEVER falls through to "Fairly valued", NEVER shows a
  // green/yellow/red classification, and NEVER shows a fabricated
  // 0 / 0% / 0/100.
  const isUnavailable =
    valuation.status === "Unavailable" || valuation.signal === "Unavailable"
    || valuation.classification === "unavailable";
  // Correction Block 2 review follow-up (#3) — every consumer inside
  // ScorePanel derives from the CANONICAL classification when it is
  // present.  This is the single source of truth; the legacy
  // ``valuation.signal`` and ``valuation.status`` fields remain as
  // safe fallbacks for pre-canonical cached rows.
  const canonClassification = valuation.classification || null;
  const canonSignal = (() => {
    if (canonClassification === "top_opportunity" || canonClassification === "opportunity") return "Opportunity";
    if (canonClassification === "risk") return "Risk";
    if (canonClassification === "unavailable") return "Unavailable";
    if (canonClassification === "fair" || canonClassification === "neutral") return "Neutral";
    return valuation.signal || "Neutral";
  })();
  // Legacy-shape valuation object with the canonical signal reconciled
  // so getDisplayTokens picks the correct color even when the raw
  // ``signal`` field disagrees with the canonical classification.
  const canonValuation = canonClassification
    ? { ...valuation, signal: canonSignal }
    : valuation;
  const signalTok = getDisplayTokens(canonValuation);        // drives card color
  const statusTok = getStatusTokens(valuation.status);       // drives valuation pill
  const sigCfg = getSignalTokens(canonSignal);
  // Translate headline based on the canonical classification when
  // available; the legacy ``status`` string is used only as a
  // fallback for pre-canonical cached rows so a rerun cannot show
  // "Fairly valued" alongside an Opportunity chip.
  const headline = (() => {
    if (isUnavailable) return t("analyze.headline.unavailable");
    const up = valuation.upside_pct;
    const upAbs = Math.abs(Number(up) || 0).toFixed(1);
    if (canonClassification === "fair" || canonClassification === "neutral") {
      return t("analyze.headline.fairly_valued");
    }
    if (canonClassification === "risk") {
      // Distinguish "overvalued" from "elevated risk" per the same
      // rule the backend headline helper uses.
      if (Number.isFinite(Number(up)) && Number(up) <= -15.0) {
        return t("analyze.headline.overvalued", { pct: upAbs });
      }
      return t("analyze.headline.elevated_risk") || t("analyze.headline.overvalued", { pct: upAbs });
    }
    if (canonClassification === "top_opportunity" || canonClassification === "opportunity") {
      return t("analyze.headline.undervalued", { pct: upAbs });
    }
    // Fallback to the legacy status-based headline for pre-canonical
    // cached rows without a persisted classification.
    if (valuation.status === "Undervalued") return t("analyze.headline.undervalued", { pct: upAbs });
    if (valuation.status === "Overvalued") return t("analyze.headline.overvalued", { pct: upAbs });
    if (valuation.status === "Fair") return t("analyze.headline.fairly_valued");
    return "\u2014";
  })();
  // Translate signal label — canonical-first.
  const signalLabel = (() => {
    if (isUnavailable) return t("analyze.signal.unavailable");
    if (canonSignal === "Opportunity") return t("analyze.signal.opportunity");
    if (canonSignal === "Risk") return t("analyze.signal.risk");
    return t("analyze.signal.neutral");
  })();
  // Pre-launch terminology correction (2026-02-09) — the hero chip
  // renders the AI Opportunity Score TIER ("Opportunity Level"),
  // not the canonical multi-factor classification.  This keeps the
  // three concepts on the analysis page distinct:
  //   * AI Opportunity Score        — the 0-100 number
  //   * Opportunity Level (chip)    — the score TIER (Low/Med/High)
  //   * Risk Level (chip)           — the GENUINE financial risk
  //   * Overall classification      — still the top_opportunity mini-badge
  // Thresholds mirror backend/poc/valuation_guards.py:
  //   <45 → Low, 45-74 → Medium, >=75 → High.
  const opportunityLevelKey = (() => {
    if (isUnavailable) return "unavailable";
    const n = Number(valuation.ai_score);
    if (!Number.isFinite(n)) return "unavailable";
    if (n < 45) return "low";
    if (n < 75) return "medium";
    return "high";
  })();
  const opportunityLevelLabel = t(
    "analyze.opportunity_level." + opportunityLevelKey,
  );
  // Translate valuation status label
  const valuationStatusLabel = isUnavailable
    ? t("analyze.valuation_status.unavailable")
    : valuation.status
    ? (t("analyze.valuation_status." + valuation.status) || valuation.status)
    : "\u2014";
  // Translate risk level
  const riskLevelLabel = valuation.risk_level
    ? (t("analyze.status." + valuation.risk_level.toLowerCase()) || valuation.risk_level)
    : "\u2014";
  const Up = valuation.upside_pct > 0 ? ArrowUpRight : valuation.upside_pct < 0 ? ArrowDownRight : Minus;
  const subs = valuation.sub_scores || {};
  // Phase 6.AH null-truthfulness — never produce a numeric delta from
  // mixed null/undefined operands. Treat as "unknown" when either fair
  // value or price is missing so the secondary line below renders "—"
  // instead of "+$NaN vs price".
  const rawDelta = valuation.fair_value_delta;
  const rawFv = valuation.fair_value;
  const rawPrice = price;
  let delta = null;
  if (Number.isFinite(Number(rawDelta))) {
    delta = Number(rawDelta);
  } else if (
    Number.isFinite(Number(rawFv)) &&
    Number.isFinite(Number(rawPrice))
  ) {
    delta = Number(rawFv) - Number(rawPrice);
  }
  const deltaKnown = delta !== null;
  const deltaSign = deltaKnown ? (delta >= 0 ? "+" : "−") : "";

  // Pre-launch terminology correction (2026-02-09) — chip visuals
  // now reflect the Opportunity LEVEL (score tier) so the chip
  // colour+icon cannot silently contradict its own label.  The
  // panel's overall border/glow (`signalTok`) still tracks the
  // canonical classification, which is the correct source for the
  // overall look.
  const OpportunityLevelIcon = opportunityLevelKey === "high"
    ? Sparkles
    : opportunityLevelKey === "low"
      ? AlertTriangle
      : Minus;
  const opportunityLevelChipStyle = (() => {
    if (opportunityLevelKey === "high") {
      return {
        color: "#3FAE89",
        background: "rgba(63,174,137,0.10)",
        borderColor: "rgba(63,174,137,0.32)",
      };
    }
    if (opportunityLevelKey === "low") {
      return {
        color: "#E5484D",
        background: "rgba(229,72,77,0.10)",
        borderColor: "rgba(229,72,77,0.32)",
      };
    }
    // medium / unavailable
    return {
      color: "#E0B341",
      background: "rgba(224,179,65,0.10)",
      borderColor: "rgba(224,179,65,0.32)",
    };
  })();

  return (
    <div
      className="relative overflow-hidden rounded-2xl border bg-card/60 p-5 sm:p-7 shadow-[var(--app-shadow-elev-2)]"
      style={{ borderColor: signalTok.border }}
      data-testid="score-panel"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: `radial-gradient(800px 200px at 0% 0%, ${signalTok.glow}, transparent 70%)` }}
      />
      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
        {/* Big AI Opportunity Score */}
        <motion.div
          key={`score-${animateKey || valuation.ai_score}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="lg:col-span-4 flex flex-col items-start"
        >
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("analyze.hero.ai_opportunity_score")}</div>
          <div className="flex items-baseline gap-2 mt-1">
            <span
              className="font-display text-7xl sm:text-8xl font-semibold tabular leading-none"
              style={{ color: signalTok.fg }}
              data-testid="score-panel-ai-score"
            >
              {(valuation.ai_score === null || valuation.ai_score === undefined || !Number.isFinite(Number(valuation.ai_score)))
                ? "—"
                : valuation.ai_score}
            </span>
            <span className="text-2xl text-muted-foreground tabular">{t("analyze.hero.out_of_100")}</span>
          </div>
          {/* Opportunity Level chip — Sub-pass terminology correction
              (2026-02-09).  Renders the AI Opportunity Score TIER
              (Low/Medium/High), NOT the canonical classification.
              This keeps the three concepts on the analysis page
              cleanly disentangled: the score number, this tier
              chip, the genuine Risk Level chip, and the overall
              classification (rendered further down as the Top
              Opportunity mini-badge). */}
          <div
            className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border"
            style={opportunityLevelChipStyle}
            data-testid="score-panel-opportunity-level-chip"
          >
            <OpportunityLevelIcon className="w-3 h-3" />
            {t("analyze.hero.opportunity_level_label")} · {opportunityLevelLabel}
          </div>
          {/* Phase 6.4 — Canonical classification chip.  Shown only
              when the backend emitted the classification field.
              Frontend NEVER derives this locally.  Top Opportunity
              uses a slightly stronger green treatment. */}
          {valuation.classification === "top_opportunity" ? (
            <div
              className="mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold"
              style={{
                color: "#0F3D2E",
                background: "linear-gradient(90deg, rgba(63,174,137,0.28), rgba(63,174,137,0.16))",
                border: "1px solid rgba(63,174,137,0.55)",
              }}
              data-testid="score-panel-classification-top-opportunity"
            >
              {t("analyze.classification.top_opportunity") || "Top Opportunity"}
            </div>
          ) : null}
        </motion.div>

        {/* Headline + price/fair value */}
        <motion.div
          key={`headline-${animateKey || valuation.fair_value}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.1 }}
          className="lg:col-span-5"
        >
          <div
            className="font-display text-3xl sm:text-4xl tracking-tight leading-tight flex items-center gap-2 flex-wrap"
            data-testid="score-panel-headline"
            style={{ color: signalTok.fg }}
          >
            <Up className="w-7 h-7" />
            <span>{headline}</span>
          </div>

          {/* Secondary chips — Valuation classification + Risk level.
              Phase 6.BI-20: Removed the leading colored dot. The chip's
              tinted background + border + text colour already convey
              the status; the small dot was a decorative artifact that
              looked unfinished alongside the other clean badges. */}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium"
              style={{ color: statusTok.fg, background: statusTok.bg, border: `1px solid ${statusTok.border}` }}
              data-testid="score-panel-valuation-chip"
            >
              {t("analyze.hero.valuation_label")} · {valuationStatusLabel}
            </span>
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium border"
              style={riskChipStyle(valuation.risk_level)}
              data-testid="score-panel-risk-chip"
            >
              {t("analyze.hero.risk_level_label")} · {riskLevelLabel}
            </span>
            {valuation.signal === "Opportunity" && valuation.status !== "Undervalued" ? null : null}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-border/70 bg-card/60 p-3">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("analyze.hero.current_price")}</div>
              <div className="mt-1 font-mono tabular text-xl">${fmtPrice(price)}</div>
            </div>
            <div className="rounded-xl border border-border/70 bg-card/60 p-3">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("analyze.hero.fair_value")}</div>
              <div className="mt-1 font-mono tabular text-xl" style={{ color: statusTok.fg }} data-testid="score-panel-fair-value">
                {isUnavailable || !Number.isFinite(Number(valuation.fair_value))
                  ? "\u2014"
                  : `$${fmtPrice(valuation.fair_value)}`}
              </div>
              {isUnavailable ? (
                <div
                  className="text-[11px] tabular mt-0.5 text-muted-foreground"
                  data-testid="score-panel-unavailable-reason"
                >
                  {t("analyze.hero.valuation_unavailable")}
                </div>
              ) : (
                <div className="text-[11px] tabular mt-0.5" style={{ color: statusTok.fg }} data-testid="score-panel-dollar-delta">
                  {deltaKnown ? t("analyze.hero.delta_vs_price", { sign: deltaSign, amount: fmtPrice(Math.abs(delta)) }) : t("analyze.hero.delta_unavailable")} · {t("analyze.hero.range", { low: fmtPrice(valuation.fair_value_low), high: fmtPrice(valuation.fair_value_high) })}
                </div>
              )}
            </div>
          </div>
          <DecisionStrip valuation={valuation} className="mt-3" />
        </motion.div>

        {/* Four sub-score bars — Valuation 40% / Quality 25% / Growth 20% / Safety 15%
            Phase 8.LP.TIPS — each bar is wrapped in a Radix Tooltip
            with a clear user-facing explanation. Tooltips use the
            same collision-aware positioning as the ``snapshot``
            tile tips, so long copy near the right viewport edge
            re-flows toward the left without clipping. */}
        <TooltipProvider delayDuration={150}>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.18 }}
            className="lg:col-span-3 grid grid-cols-2 lg:grid-cols-1 gap-2"
          >
            <SubScoreBar
              label={t("analyze.hero.subscore_valuation")}
              weight="40%"
              value={subs.valuation}
              testId="subscore-valuation"
              tipTitle={t("analyze.subscore_tips.valuation_title")}
              tipBody={t("analyze.subscore_tips.valuation_body")}
            />
            <SubScoreBar
              label={t("analyze.hero.subscore_quality")}
              weight="25%"
              value={subs.quality ?? subs.health}
              testId="subscore-quality"
              tipTitle={t("analyze.subscore_tips.quality_title")}
              tipBody={t("analyze.subscore_tips.quality_body")}
            />
            <SubScoreBar
              label={t("analyze.hero.subscore_growth")}
              weight="20%"
              value={subs.growth}
              testId="subscore-growth"
              tipTitle={t("analyze.subscore_tips.growth_title")}
              tipBody={t("analyze.subscore_tips.growth_body")}
            />
            <SubScoreBar
              label={t("analyze.hero.subscore_safety")}
              weight="15%"
              value={subs.safety}
              testId="subscore-safety"
              tipTitle={t("analyze.subscore_tips.safety_title")}
              tipBody={t("analyze.subscore_tips.safety_body")}
            />
          </motion.div>
        </TooltipProvider>
      </div>
    </div>
  );
}

function SubScoreBar({ label, weight, value, testId, tipTitle, tipBody }) {
  // Phase 6.AI — defensively coerce value before chaining toFixed.
  // Feb 2026 — `Number(null)` returns 0 (finite!), which would
  // silently substitute a fake "0" for a genuinely null sub-score
  // (e.g. valuation sub-score in the Unavailable contract).  Reject
  // null / undefined explicitly BEFORE the numeric coercion so a
  // null valuation sub-score renders em-dash instead of "0".
  const raw = (value === null || value === undefined) ? null : Number(value);
  const v = (raw !== null && Number.isFinite(raw))
    ? Math.max(0, Math.min(100, raw))
    : null;
  const color = v == null
    ? "#7a7a7a"
    : v >= 65 ? "#3FAE89"
    : v >= 45 ? "#E0B341"
    : "#E5484D";
  const display = v == null ? "—" : v.toFixed(0);
  const barWidth = v == null ? 0 : v;
  const inner = (
    <div className="rounded-lg border border-border/60 bg-card/40 p-2.5 cursor-help" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {label} <span className="text-muted-foreground/60 font-normal normal-case">· {weight}</span>
        </span>
        <span className="font-mono tabular text-sm" style={{ color }}>{display}</span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-muted/40 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${barWidth}%`, background: color }} />
      </div>
    </div>
  );
  // No tooltip if the caller didn't provide copy (backward compat).
  if (!tipTitle && !tipBody) return inner;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{inner}</TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        sideOffset={8}
        collisionPadding={12}
        avoidCollisions
        className="max-w-[320px] text-[12px] leading-[1.55] bg-popover/95 backdrop-blur-xl border border-border/70 shadow-[var(--app-shadow-elev-2)] z-50"
        data-testid={`${testId}-tooltip`}
      >
        {tipTitle ? (
          <div
            className="font-medium text-foreground mb-1"
            data-testid={`${testId}-tooltip-title`}
          >
            {tipTitle}
          </div>
        ) : null}
        {tipBody ? (
          <div
            className="text-muted-foreground/95"
            data-testid={`${testId}-tooltip-body`}
          >
            {tipBody}
          </div>
        ) : null}
      </TooltipContent>
    </Tooltip>
  );
}

function riskChipStyle(level) {
  if (level === "Low") {
    return {
      color: "#3FAE89",
      background: "rgba(63,174,137,0.10)",
      borderColor: "rgba(63,174,137,0.32)",
    };
  }
  if (level === "High") {
    return {
      color: "#E5484D",
      background: "rgba(229,72,77,0.10)",
      borderColor: "rgba(229,72,77,0.32)",
    };
  }
  // Medium / unknown
  return {
    color: "#E0B341",
    background: "rgba(224,179,65,0.10)",
    borderColor: "rgba(224,179,65,0.32)",
  };
}
